/**
 * AuthGuard — Initializes Keycloak and provides auth-aware rendering.
 *
 * Wraps the entire app. On mount it initializes the Keycloak adapter
 * (check-sso mode), which silently checks if the user has an active
 * Keycloak session without forcing a redirect.
 *
 * Children receive auth state via the Zustand auth store.
 */

import React, { useEffect, useState } from "react";
import { useAuthStore } from "@/stores/auth-store";

interface AuthGuardProps {
  children: React.ReactNode;
  loadingComponent?: React.ReactNode;
}

function AuthGuard({ children, loadingComponent }: AuthGuardProps) {
  const initialize = useAuthStore((s) => s.initialize);
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    initialize().catch((err) => {
      console.error("Keycloak init error:", err);
      setError("Authentication service unavailable. Please try again later.");
    });
  }, [initialize]);

  if (error) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          fontFamily: "sans-serif",
          color: "#333",
        }}
      >
        <h2>Authentication Error</h2>
        <p>{error}</p>
        <button
          onClick={() => window.location.reload()}
          style={{
            marginTop: 16,
            padding: "8px 24px",
            backgroundColor: "#1F4E79",
            color: "white",
            border: "none",
            borderRadius: 6,
            cursor: "pointer",
            fontSize: 14,
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!isInitialized) {
    return (
      <>
        {loadingComponent || (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100vh",
              fontFamily: "sans-serif",
              color: "#666",
            }}
          >
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  border: "3px solid #e0e0e0",
                  borderTopColor: "#1F4E79",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                  margin: "0 auto 16px",
                }}
              />
              <p>Loading CollabKare...</p>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          </div>
        )}
      </>
    );
  }

  return <>{children}</>;
}

export default AuthGuard;
